/**
 * Cee-Lo Game Rules
 *
 * Street rules implementation for 3-dice Cee-Lo
 * Shared between client (UI feedback) and server (authoritative)
 */
import { CeeloResultType } from '../state-machine/GameStates.js';
export var CeeloSideBetType;
(function (CeeloSideBetType) {
    CeeloSideBetType["BANK_WINS"] = "bank_wins";
    CeeloSideBetType["BANK_LOSES"] = "bank_loses";
    CeeloSideBetType["FOUR_UP"] = "four_up";
    CeeloSideBetType["THREE_DOWN"] = "three_down";
})(CeeloSideBetType || (CeeloSideBetType = {}));
export const CEELO_SIDE_BETS = {
    [CeeloSideBetType.BANK_WINS]: {
        type: CeeloSideBetType.BANK_WINS,
        label: 'Bank Wins',
        odds: 1,
    },
    [CeeloSideBetType.BANK_LOSES]: {
        type: CeeloSideBetType.BANK_LOSES,
        label: 'Bank Loses',
        odds: 1,
    },
    [CeeloSideBetType.FOUR_UP]: {
        type: CeeloSideBetType.FOUR_UP,
        label: '4+',
        odds: 1,
    },
    [CeeloSideBetType.THREE_DOWN]: {
        type: CeeloSideBetType.THREE_DOWN,
        label: '3 or Less',
        odds: 1,
    },
};
// Result rankings (higher = better)
const RANK = {
    ACE: 1, // 1-2-3: Instant loss
    PAIR_1: 2,
    PAIR_2: 3,
    PAIR_3: 4,
    PAIR_4: 5,
    PAIR_5: 6,
    PAIR_6: 7,
    TRIPS_1: 8,
    TRIPS_2: 9,
    TRIPS_3: 10,
    TRIPS_4: 11,
    TRIPS_5: 12,
    TRIPS_6: 13, // Triple 6: highest trip, also an instant win
    CEELO: 14, // 4-5-6: Instant win, game's namesake roll — the top of the table
};
/**
 * Evaluate a 3-dice roll for Cee-Lo
 */
export function evaluateCeeloRoll(dice) {
    // Sort dice for easier pattern matching
    const sorted = [...dice].sort((a, b) => a - b);
    const [d1, d2, d3] = sorted;
    // Check for Ace (1-2-3) - instant loss
    if (d1 === 1 && d2 === 2 && d3 === 3) {
        return {
            dice,
            type: CeeloResultType.ACE,
            value: 0,
            rank: RANK.ACE,
        };
    }
    // Check for 4-5-6 — instant win, the roll cee-lo is named after
    if (d1 === 4 && d2 === 5 && d3 === 6) {
        return {
            dice,
            type: CeeloResultType.CEELO,
            value: 0,
            rank: RANK.CEELO,
        };
    }
    // Check for trips (three of a kind). Triple 6 is a separate type —
    // it's the highest trip and also counts as an instant win.
    if (d1 === d2 && d2 === d3) {
        const tripValue = d1;
        const isTrips6 = tripValue === 6;
        return {
            dice,
            type: isTrips6 ? CeeloResultType.TRIPS_6 : CeeloResultType.TRIPS,
            value: tripValue,
            rank: isTrips6 ? RANK.TRIPS_6 : RANK.TRIPS_1 + (tripValue - 1),
        };
    }
    // Check for pair
    let pairValue = null;
    let oddDie = null;
    if (d1 === d2) {
        pairValue = d1;
        oddDie = d3;
    }
    else if (d2 === d3) {
        pairValue = d2;
        oddDie = d1;
    }
    else if (d1 === d3) {
        pairValue = d1;
        oddDie = d2;
    }
    if (pairValue !== null && oddDie !== null) {
        return {
            dice,
            type: CeeloResultType.PAIR,
            value: oddDie, // The point value is the odd die
            rank: RANK.PAIR_1 + (oddDie - 1),
        };
    }
    // No scoring combination - player must re-roll
    return {
        dice,
        type: CeeloResultType.NOTHING,
        value: 0,
        rank: 0,
    };
}
/**
 * Compare two Cee-Lo results
 * Returns: positive if a wins, negative if b wins, 0 if tie
 */
export function compareCeeloResults(a, b) {
    return a.rank - b.rank;
}
/**
 * Check if a result is an instant win (Cee-Lo = 4-5-6, or Triple 6s).
 */
export function isInstantWin(result) {
    return result.type === CeeloResultType.CEELO ||
        result.type === CeeloResultType.TRIPS_6;
}
/**
 * Check if a result is an instant loss (Ace)
 */
export function isInstantLoss(result) {
    return result.type === CeeloResultType.ACE;
}
/**
 * Check if player needs to re-roll (no scoring combo)
 */
export function needsReroll(result) {
    return result.type === CeeloResultType.NOTHING;
}
/**
 * Get display name for a result
 */
export function getCeeloResultName(result) {
    switch (result.type) {
        case CeeloResultType.ACE:
            return 'ACE (1-2-3)';
        case CeeloResultType.PAIR:
            return `Pair - Point ${result.value}`;
        case CeeloResultType.TRIPS:
            return `Trips ${result.value}s`;
        case CeeloResultType.TRIPS_6:
            return 'Triple 6s';
        case CeeloResultType.CEELO:
            return 'CEE-LO! (4-5-6)';
        case CeeloResultType.NOTHING:
            return 'No Point - Re-roll';
        default:
            return 'Unknown';
    }
}
/**
 * Calculate payout multiplier for banker vs player
 * Basic street rules: 1:1 payouts
 * Can be extended for side bets
 */
export function calculatePayout(bankerResult, playerResult, betAmount) {
    const comparison = compareCeeloResults(playerResult, bankerResult);
    if (comparison > 0) {
        // Player wins - gets their bet back + winnings
        return betAmount * 2;
    }
    else if (comparison < 0) {
        // Banker wins - player loses bet
        return 0;
    }
    else {
        // Tie - push, player gets bet back
        return betAmount;
    }
}
export function getCeeloRollTotal(result) {
    return result.dice.reduce((sum, value) => sum + value, 0);
}
export function evaluateCeeloSideBet(type, bankerResult, playerResult) {
    if (!bankerResult || !playerResult)
        return false;
    const comparison = compareCeeloResults(playerResult, bankerResult);
    switch (type) {
        case CeeloSideBetType.BANK_WINS:
            return comparison < 0 || isInstantWin(bankerResult) || isInstantLoss(playerResult);
        case CeeloSideBetType.BANK_LOSES:
            return comparison > 0 || isInstantLoss(bankerResult) || isInstantWin(playerResult);
        case CeeloSideBetType.FOUR_UP: {
            // Wins on: point >= 4, any trips (including 6s), or cee-lo (trips always beat points)
            const t = playerResult.type;
            if (t === CeeloResultType.CEELO)
                return true;
            if (t === CeeloResultType.TRIPS_6)
                return true;
            if (t === CeeloResultType.TRIPS)
                return true;
            if (t === CeeloResultType.PAIR)
                return playerResult.value >= 4;
            return false;
        }
        case CeeloSideBetType.THREE_DOWN: {
            // Wins on: point <= 3, or ace (1-2-3)
            const t = playerResult.type;
            if (t === CeeloResultType.ACE)
                return true;
            if (t === CeeloResultType.PAIR)
                return playerResult.value >= 1 && playerResult.value <= 3;
            return false;
        }
        default:
            return false;
    }
}
/**
 * Tie-aware sidebet evaluation. Used by per-shooter settlement where a
 * shooter↔banker rank tie is possible:
 *   - BANK_WINS / BANK_LOSES push (refund) when the duel itself is a push,
 *     since the banker neither won nor lost.
 *   - POINT_4_PLUS / POINT_3_MINUS resolve normally — they're a function of
 *     the shooter's own point, not of the banker comparison.
 *
 * `evaluateCeeloSideBet` (boolean) is preserved for callers that don't have
 * a tie context (e.g., banker-bust resolution where `playerResult` is the
 * banker's own roll).
 */
export function evaluateCeeloSideBetOutcome(type, bankerResult, playerResult) {
    if (!bankerResult || !playerResult)
        return 'lose';
    const comparison = compareCeeloResults(playerResult, bankerResult);
    // Pure tie: same rank AND no instant-win/loss bust on either side.
    const isTie = comparison === 0
        && !isInstantWin(bankerResult)
        && !isInstantLoss(bankerResult)
        && !isInstantWin(playerResult)
        && !isInstantLoss(playerResult);
    switch (type) {
        case CeeloSideBetType.BANK_WINS:
            if (isTie)
                return 'push';
            return (comparison < 0 || isInstantWin(bankerResult) || isInstantLoss(playerResult))
                ? 'win' : 'lose';
        case CeeloSideBetType.BANK_LOSES:
            if (isTie)
                return 'push';
            return (comparison > 0 || isInstantLoss(bankerResult) || isInstantWin(playerResult))
                ? 'win' : 'lose';
        default:
            return evaluateCeeloSideBet(type, bankerResult, playerResult) ? 'win' : 'lose';
    }
}
/**
 * Validate dice values (1-6)
 */
export function validateDice(dice) {
    return dice.every(d => Number.isInteger(d) && d >= 1 && d <= 6);
}
//# sourceMappingURL=CeeloRules.js.map